/**
 * usePwa.js
 *
 * Single source of truth for PWA service-worker state.
 *
 * Uses `virtual:pwa-register/vue` — the official Vue-reactive binding
 * provided by vite-plugin-pwa.  It gives us reactive `needRefresh` and
 * `offlineReady` refs so we never have to dispatch / listen to custom
 * window events manually.
 *
 * Update behaviour
 * ────────────────
 * • Cold-start  — if a SW is already waiting when the page loads, the update
 *   prompt is shown once. If the user has already dismissed it this tab session
 *   (via sessionStorage), the prompt is suppressed — preventing the loop that
 *   occurs when rapid deployments create a new waiting SW on every page load.
 * • Hourly check — if the periodic fetch finds a new SW *while the user is
 *   already inside the app* (≥ 1 h session), the prompt is shown once so
 *   they can choose when to reload.
 *
 * Usage
 * ─────
 *   const { needRefresh, offlineReady, updateServiceWorker } = usePwa()
 */

import { computed, effectScope, readonly, ref, watch } from 'vue';
import { isTma } from '@/platform/index.js';

// Lazy-loaded so the virtual module is never imported in TMA / SSR contexts.
let _registered = false;
let _updateSW   = null;
let _updateInterval = null;
let _scope = null;
// One-shot guard: prevents stacked `controlling` listeners (upstream
// vite-plugin-pwa adds them without `{ once: true }`) from calling
// location.reload() more than once per SW activation.
let _updateInFlight = false;

const needRefresh  = ref(false);
const offlineReady = ref(false);
const updateError  = ref(null);

// Session-scoped dismiss flag: set when the user taps "Later" on any update
// prompt. Persisted to sessionStorage so it survives the SW-triggered reload
// (which resets module-level JS state). Prevents the same prompt from
// appearing again in the same tab session even if rapid deployments keep
// creating new waiting SWs on every page load.
// sessionStorage is per-tab and clears on tab close, so future sessions always
// get a fresh chance to see the update prompt.
const _SS_KEY = 'pwa:dismissed';
const _dismissed = ref(typeof sessionStorage !== 'undefined'
  ? sessionStorage.getItem(_SS_KEY) === '1'
  : false);

// ── Reload-hold registry ──────────────────────────────────────────────────────
// Surfaces that must NOT be interrupted by a SW reload (in-flight AI streams,
// dirty forms, checkout flows) acquire a hold via `holdReload(reason)`. The
// update prompt in App.vue defers while `isReloadBusy.value === true` and
// re-fires when the count drops back to 0. A counter is enough — the `reason`
// argument is documented for future per-token diagnostics but unused today.
// PWA playbook Section 3 "Update during AI stream"; Section 5 row 6.
const _reloadHoldCount = ref(0);
const isReloadBusy     = computed(() => _reloadHoldCount.value > 0);

/**
 * Acquire a "do not reload" hold. Returns a release function that is safe to
 * call multiple times (only the first call decrements the counter). Pair the
 * acquisition with a release in `onScopeDispose` / cleanup so a navigation
 * away does not strand a hold.
 */
function holdReload(_reason = 'unspecified') {
  _reloadHoldCount.value += 1;
  let released = false;
  return function releaseReloadHold() {
    if (released) return;
    released = true;
    _reloadHoldCount.value -= 1;
  };
}

/**
 * Call once — idempotent.  Registers the SW and wires up the reactive refs.
 * Called from PwaAdapter.registerServiceWorker() in production only.
 */
async function register() {
  // Defense-in-depth against TMA: TmaAdapter.registerServiceWorker() is
  // already a no-op, but if any future caller imports usePwa() directly the
  // composable itself must refuse to register inside Telegram. SW registration
  // in TMA breaks the platform invariant (see PWA playbook Section 1, Section 5 row 9).
  if (isTma()) return;

  if (_registered) return;
  _registered = true;

  try {
    // virtual:pwa-register/vue is a Vite virtual module injected by vite-plugin-pwa.
    // It exposes useRegisterSW() which returns Vue refs wired to the SW lifecycle.
    const { useRegisterSW } = await import('virtual:pwa-register/vue');

    const result = useRegisterSW({
      // Fire once immediately, then every hour if the user is online.
      immediate: true,

      onRegisteredSW(swUrl, registration) {
        if (!registration) return;

        // Periodic update check (hourly). Handle captured so unregister()
        // can clear it; otherwise it leaks across HMR reloads in dev.
        //
        // Robust form (PWA playbook Section 3): probe the SW URL with `no-store`
        // first and only call `registration.update()` on a 200 response.
        // The simpler form — calling `update()` unconditionally — treats
        // a 404 (CDN rollback, mid-deploy window) as "SW removed" and
        // unregisters the active SW, silently dropping offline support
        // until next page load. The probe avoids that.
        _updateInterval = setInterval(async () => {
          // Skip if a SW is already installing or waiting. Calling update()
          // while registration.waiting is set causes workbox-window to
          // re-emit the `waiting` event, which stacks another `controlling`
          // listener and triggers an extra location.reload().
          if (registration.installing || registration.waiting || !navigator.onLine) return;
          try {
            const resp = await fetch(swUrl, {
              cache: 'no-store',
              headers: { 'cache-control': 'no-cache' },
            });
            if (resp?.status === 200) {
              await registration.update();
            }
          } catch (err) {
            console.warn('[PWA] Periodic update check failed:', err);
          }
        }, 60 * 60 * 1_000);
      },

      onRegisterError(error) {
        console.error('[PWA] SW registration failed:', error);
        updateError.value = error;
      },
    });

    // Bridge the composable refs into our own singletons so the whole app
    // can import from this file without caring about useRegisterSW internals.
    _updateSW = result.updateServiceWorker;

    // Watch inside an effectScope so the watchers can be stopped on
    // unregister() / HMR teardown. Static `import { watch }` (vs. the
    // previous dynamic `import('vue').then(...)`) keeps tree-shaking
    // intact and gives the watchers a real scope owner.
    _scope = effectScope(true);
    _scope.run(() => {
      watch(result.needRefresh,  (v) => { needRefresh.value  = v; });
      watch(result.offlineReady, (v) => { offlineReady.value = v; });
    });

  } catch (err) {
    console.error('[PWA] SW import failed:', err);
    updateError.value = err;
  }
}

/**
 * Stop the hourly update check and the bridged watchers. Idempotent.
 * Vite HMR fires this during dev module reloads; production callers
 * generally do not need it.
 */
function unregister() {
  if (_updateInterval) {
    clearInterval(_updateInterval);
    _updateInterval = null;
  }
  if (_scope) {
    _scope.stop();
    _scope = null;
  }
  _registered      = false;
  _updateSW        = null;
  _updateInFlight  = false;

  // Reset the bridged refs so a subsequent register() (e.g. after HMR)
  // produces fresh `change` events. Vue's `watch` only fires on transition,
  // so leaving these at their last value would cause App.vue's update-prompt
  // and offline-toast watchers to silently no-op after a re-register cycle.
  // PWA audit LOW-2.
  needRefresh.value  = false;
  offlineReady.value = false;
  // Do NOT reset _dismissed here: sessionStorage is the source of truth and
  // persists across HMR reloads intentionally. Clearing it on unregister()
  // would re-show the update dialog after every HMR cycle in dev:tunnel.
  _dismissed.value   = typeof sessionStorage !== 'undefined'
    ? sessionStorage.getItem(_SS_KEY) === '1'
    : false;
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => { unregister(); });
}

/**
 * Trigger the waiting SW to skip-wait and reload all clients.
 * Pass `reloadPage = true` to force an immediate hard reload.
 */
async function updateServiceWorker(reloadPage = true) {
  if (!_updateSW) {
    console.warn('[PWA] updateServiceWorker called before SW is registered');
    return;
  }
  // One-shot guard: the upstream vite-plugin-pwa `controlling` listener is added
  // without `{ once: true }`, so if it was called N times in a session, N
  // listeners each call location.reload(). Clamping to one in-flight update
  // per page lifetime prevents the race.
  if (_updateInFlight) return;
  _updateInFlight = true;

  try {
    await _updateSW(reloadPage);
  } catch (err) {
    console.error('[PWA] SW update failed:', err);
    updateError.value = err;
  }
}

/**
 * Dismiss the "offline ready" notification without reloading.
 */
function dismissOfflineReady() {
  offlineReady.value = false;
}

/**
 * Dismiss the "update available" prompt without applying the update.
 * Persisted to sessionStorage so the flag survives SW-triggered page reloads
 * within the same browser tab (sessionStorage outlives JS context resets).
 * Clears on tab close so future sessions always get a fresh prompt.
 */
function dismissUpdate() {
  _dismissed.value = true;
  try { sessionStorage.setItem(_SS_KEY, '1'); } catch { /* quota/private noop */ }
}

export function usePwa() {
  return {
    /** true when a new SW is waiting to activate */
    needRefresh:      readonly(needRefresh),
    /** true when the app shell is cached and ready for offline use */
    offlineReady:     readonly(offlineReady),
    /** non-null if registration or update threw */
    updateError:      readonly(updateError),
    /** true while any caller has acquired a reload-hold (see holdReload) */
    isReloadBusy,
    /** true after the user tapped "Later" on the update prompt (survives SW-triggered reloads) */
    isDismissed:      readonly(_dismissed),

    register,
    unregister,
    updateServiceWorker,
    dismissOfflineReady,
    dismissUpdate,
    holdReload,
  };
}
